import * as React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Typography, makeStyles, Button, Collapse, Grid } from '@material-ui/core';
import { useIifImportSelector, IifImportSelectors } from '../store/modules/iifImport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { DateTime } from 'luxon'
import { classNames } from '../shared';
import ExpenseDistributionViews from './ExpenseDistributionsView';
import AudCurrencyFormatter from '../utilities/currencyFormatter';
import { useApplicationSettingsSelector, ApplicationSettingsSelectors, TaxMode } from '../store/modules/applicationSettings';
import SalesItemDistributionViews from './SalesItemDistributionsView';
import { red, yellow, green } from '@material-ui/core/colors';
import AttachmentViews from './AttachmentsView';
import DepositDistributionViews from './DepositDistributionsView';

interface IDepositViewProps {
    fileIndex: number;
    depositIndex: number;
    depositPosition: number;
    importSuccess: boolean;
    importError: boolean;
}

const useStyles = makeStyles(theme => ({
    error: {
        background: `${red[100]} !important`
    },
    financialError: {
        background: `${theme.palette.warning.dark} !important`,
        color: `${theme.palette.common.white} !important`
    },
    warning: {
        '& td': {
            background: yellow[100]
        }
    },
    errorRow: {
        '& td': {
            background: red[100]
        }
    },
    successRow: {
        '& td': {
            background: green[100]
        }
    },
    button: {
        textAlign: 'center',
        minWidth: 'auto',
        padding: theme.spacing(1),

        '& svg': {
            transition: 'all 0.25s linear'
        }
    },
    expanded: {
        '& svg': {
            transform: 'rotate(180deg)'
        }
    },
    noPadding: {
        padding: theme.spacing(0),
        paddingRight: `${theme.spacing(0)} !important`
    },
    padding: {
        padding: theme.spacing(2)
    }
}))

const DepositView: React.FunctionComponent<IDepositViewProps> = ({
    fileIndex,
    depositIndex,
    depositPosition,
    importError,
    importSuccess
}) => {
    const deposit = useIifImportSelector(state => IifImportSelectors.selectDeposit(state, depositIndex));

    const taxMode = useApplicationSettingsSelector(ApplicationSettingsSelectors.selectTaxMode);

    const classes = useStyles();
    const [expanded, setExpanded] = React.useState(false);
    const [show, setShow] = React.useState(false);

    const onExpandClick = React.useCallback(() => {
        if (expanded) {
            setShow(!show);
        }
        else {
            setExpanded(true);
            setShow(true);
        }
    }, [setShow, show, expanded, setExpanded]);

    const onExited = React.useCallback(() => {
        setExpanded(false);
    }, [setExpanded])

    if (!deposit) {
        return null;
    }

    return (
        <>
            <TableRow className={classNames({ [classes.warning]: deposit.distributionErrors || deposit.attachmentErrors || !deposit.attachmentsDownloaded || deposit.financialYearError }, { [classes.successRow]: importSuccess }, { [classes.errorRow]: importError })}>
                <TableCell>
                    <Button
                        className={classNames(classes.button, { [classes.expanded]: show })}
                        color="inherit"
                        onClick={onExpandClick}
                    >
                        <FontAwesomeIcon icon={faChevronDown} fixedWidth />
                    </Button>
                </TableCell>
                <TableCell>{depositPosition}</TableCell>
                <TableCell className={classNames({ [classes.financialError]: deposit.financialYearError })}>{deposit.transactionDate ? DateTime.fromISO(deposit.transactionDate).toFormat('dd/MM/yyyy') : null}</TableCell>
                <TableCell className={classNames({ [classes.error]: deposit.accountError })}>{deposit.depositToAccount?.value}</TableCell>
                <TableCell>{deposit.docNumber}</TableCell>
                <TableCell className={classNames({ [classes.error]: deposit.currencyError })}>{deposit.currency?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: deposit.amountError })} align="right">
                    {
                        taxMode === TaxMode.Excluded ?
                            AudCurrencyFormatter.format(deposit.distributionAmount ?? 0) :
                            AudCurrencyFormatter.format(deposit.distributionTotal ?? 0)
                    }
                </TableCell>
            </TableRow>
            {
                expanded && (
                    <TableRow>
                        <TableCell className={classes.noPadding} />
                        <TableCell colSpan={6} className={classes.noPadding}>
                            <Collapse in={show} appear={true} onExited={onExited} >
                                <DepositDistributionViews
                                    distributionView={deposit.distributionView}
                                    amount={deposit.distributionAmount ?? 0}
                                    tax={deposit.distributionTax ?? 0}
                                    total={deposit.distributionTotal ?? 0}
                                />
                                <AttachmentViews
                                    fileIndex={fileIndex}
                                    type="deposits"
                                    typeIndex={depositIndex}
                                    attachmentView={deposit.attachmentView}
                                />
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )
            }
        </>
    );
}

export default DepositView;


// WEBPACK FOOTER //
// ./src/components/DepositView.tsx